import React, { useEffect, useRef, useState } from 'react';
import { Translate } from 'react-jhipster';
import { Alert, Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner, Table } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'app/config/store';
import { importDevices, resetImportResult } from './inventory.reducer';

type DeviceImportDialogProps = {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
};

const ACCEPTED_FILE_TYPES = '.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel';

export const DeviceImportDialog = ({ open, onClose, onImported }: DeviceImportDialogProps) => {
  const dispatch = useAppDispatch();
  const importing = useAppSelector(state => state.inventory.importing);
  const importResult = useAppSelector(state => state.inventory.importResult);

  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setFile(null);
      dispatch(resetImportResult());
    }
  }, [open]);

  useEffect(() => {
    if (importResult && importResult.importedCount > 0) {
      onImported();
    }
  }, [importResult]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files?.[0] ?? null);
  };

  const handleImport = () => {
    if (file) {
      dispatch(importDevices(file));
    }
  };

  const handleImportAnother = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    dispatch(resetImportResult());
  };

  const handleClose = () => {
    if (importing) {
      return;
    }
    onClose();
  };

  return (
    <Modal isOpen={open} toggle={handleClose} centered data-cy="deviceImportDialog">
      <ModalHeader toggle={handleClose} data-cy="deviceImportDialogHeading">
        <Translate contentKey="rainspotApp.device.import.title">Bulk Import Devices</Translate>
      </ModalHeader>
      <ModalBody>
        {importing && (
          <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '160px' }}>
            <Spinner color="primary" />
            <p className="mt-3">
              <Translate contentKey="rainspotApp.device.import.processing">Processing file, please wait...</Translate>
            </p>
          </div>
        )}

        {!importing && !importResult && (
          <>
            <p>
              <Translate contentKey="rainspotApp.device.import.instructions">
                Select an Excel file (.xlsx or .xls) containing the device inventory to import.
              </Translate>
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_FILE_TYPES}
              className="form-control"
              data-cy="deviceImportFileInput"
              onChange={handleFileChange}
            />
          </>
        )}

        {!importing && importResult && (
          <div data-cy="deviceImportResult">
            <Alert color={importResult.skippedCount > 0 ? 'warning' : 'success'}>
              <Translate
                contentKey="rainspotApp.device.import.summary"
                interpolate={{
                  total: importResult.totalRows,
                  imported: importResult.importedCount,
                  skipped: importResult.skippedCount,
                }}
              >
                {`Processed ${importResult.totalRows} row(s): ${importResult.importedCount} imported, ${importResult.skippedCount} skipped.`}
              </Translate>
            </Alert>

            {importResult.errors?.length > 0 && (
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                <Table size="sm" responsive>
                  <thead>
                    <tr>
                      <th>
                        <Translate contentKey="rainspotApp.device.import.row">Row</Translate>
                      </th>
                      <th>
                        <Translate contentKey="rainspotApp.device.serialNumber">Serial No.</Translate>
                      </th>
                      <th>
                        <Translate contentKey="rainspotApp.device.import.reason">Reason</Translate>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {importResult.errors.map(err => (
                      <tr key={err.rowNumber} data-cy="deviceImportErrorRow">
                        <td>{err.rowNumber}</td>
                        <td>{err.serialNumber ?? '-'}</td>
                        <td>{err.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleClose} disabled={importing} data-cy="deviceImportCancelButton">
          <FontAwesomeIcon icon="ban" />
          &nbsp;
          {importResult ? 'Close' : <Translate contentKey="entity.action.cancel">Cancel</Translate>}
        </Button>
        {!importResult && (
          <Button color="primary" onClick={handleImport} disabled={!file || importing} data-cy="deviceImportSubmitButton">
            <FontAwesomeIcon icon="file-import" />
            &nbsp;
            <Translate contentKey="rainspotApp.device.import.upload">Import</Translate>
          </Button>
        )}
        {importResult && (
          <Button color="primary" onClick={handleImportAnother} data-cy="deviceImportAnotherButton">
            <Translate contentKey="rainspotApp.device.import.importAnother">Import Another File</Translate>
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default DeviceImportDialog;
